import React, { useState, useEffect, useCallback, useRef } from "react";
import { Row, Col, Spin, Tooltip } from 'antd';
import { SyncOutlined } from '@ant-design/icons';
import { ProjectBuild, BuildDetailModal } from "..";
import { useAuth0 } from "../../react-auth0-spa";
import config from "../../api_config.json";
import { useAppContext } from "../../context";
import {
  estimateBuildProgress,
  mapUnifiedBuildStatus,
  resolveBuildDurationSeconds,
} from "../shared/buildProgress";
import { shouldIgnoreOperationalBuild } from "../shared/ciBuilds";
import "./StatusGrid.css";
import _ from 'lodash';

const REFRESH_INTERVAL = 30000; // 30 seconds

// Transform API data to component format
const transformBuildData = (build) => {
  const durationSeconds = resolveBuildDurationSeconds(build);

  return {
    projectName: build.projectName,
    hash: build.commitSha,
    id: build.id,
    region: build.providerData?.region || 'eu-central-1',
    buildStatus: mapUnifiedBuildStatus(build.status, build.conclusion),
    durationSeconds,
    executionTime: durationSeconds > 0 ? Math.round(durationSeconds / 60) : null,
    startedAt: build.startedAt,
    endTime: build.completedAt || build.startedAt,
    provider: build.provider,
    branch: build.branch,
    buildUrl: build.buildUrl,
    errorSummary: build.errorSummary,
    failedStep: build.failedStep,
    currentStep: build.currentStep,
    commitMessage: build.commitMessage,
    commitAuthor: build.commitAuthor,
    commitUrl: build.commitUrl,
    prNumber: build.prNumber,
    prUrl: build.prUrl,
    repositoryOwner: build.repositoryOwner,
    repositoryName: build.repositoryName,
    triggerType: build.triggerType,
    initiator: build.initiator,
  };
};

const StatusGrid = () => {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [selectedBuild, setSelectedBuild] = useState(null);
  const [countdown, setCountdown] = useState(REFRESH_INTERVAL / 1000);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const { isProd } = useAppContext();
  const { getTokenSilently } = useAuth0();
  const timeoutRef = useRef(null);

  // Fetch data function
  const fetchBuilds = useCallback(async () => {
    try {
      const token = await getTokenSilently();
      const envFilter = isProd ? 'prod' : 'dev';
      const response = await fetch(
        `${config.ciUnified[isProd ? "prod" : "dev"]}/builds?limit=50&environment=${envFilter}`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      const result = await response.json();
      return (result.data || [])
        .map(transformBuildData)
        .filter((build) => !shouldIgnoreOperationalBuild(build));
    } catch (error) {
      console.error(error);
      return [];
    }
  }, [getTokenSilently, isProd]);

  // Refresh function
  const refresh = useCallback(async (isFirstLoad = false) => {
    setIsRefreshing(true);
    if (isFirstLoad) setLoading(true);

    const newData = await fetchBuilds();
    if (newData) {
      const sortedData = _.sortBy(newData, 'endTime').reverse();
      setItems(sortedData);
    }

    setLoading(false);
    setIsRefreshing(false);
    setCountdown(REFRESH_INTERVAL / 1000);
  }, [fetchBuilds]);

  // Manual refresh handler
  const handleManualRefresh = useCallback(() => {
    if (isRefreshing) return;

    // Clear existing timeout and restart
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    refresh();

    // Schedule next auto-refresh
    timeoutRef.current = setTimeout(function schedule() {
      refresh();
      timeoutRef.current = setTimeout(schedule, REFRESH_INTERVAL);
    }, REFRESH_INTERVAL);
  }, [isRefreshing, refresh]);

  // Countdown timer
  useEffect(() => {
    const interval = setInterval(() => {
      setCountdown(prev => (prev <= 1 ? REFRESH_INTERVAL / 1000 : prev - 1));
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Initial load and auto-refresh
  useEffect(() => {
    refresh(true);

    timeoutRef.current = setTimeout(function schedule() {
      refresh();
      timeoutRef.current = setTimeout(schedule, REFRESH_INTERVAL);
    }, REFRESH_INTERVAL);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [refresh]);

  const handleBuildClick = (build) => {
    setSelectedBuild(build);
  };

  const progressPercent = ((REFRESH_INTERVAL / 1000 - countdown) / (REFRESH_INTERVAL / 1000)) * 100;

  return (
    <>
      <Spin spinning={loading}>
        <Row gutter={[16, 16]} style={{ padding: '16px' }}>
          {items.map((props, key) => (
            <Col
              key={`ProjectBuild-${key}-${isProd}`}
              xs={24}
              sm={24}
              md={12}
              lg={12}
              xl={8}
              xxl={6}
            >
              <ProjectBuild
                {...props}
                progressPercent={estimateBuildProgress(props, items)}
                onShowDetails={() => handleBuildClick(props)}
              />
            </Col>
          ))}
        </Row>
      </Spin>

      {/* Refresh indicator */}
      <Tooltip title={isRefreshing ? 'Aggiornamento...' : `Clicca per aggiornare (auto: ${countdown}s)`}>
        <div
          className={`refresh-indicator ${isRefreshing ? 'refresh-indicator--active' : ''}`}
          onClick={handleManualRefresh}
        >
          <svg className="refresh-indicator__ring" viewBox="0 0 36 36">
            <circle
              className="refresh-indicator__bg"
              cx="18" cy="18" r="16"
              fill="none"
              strokeWidth="2"
            />
            <circle
              className="refresh-indicator__progress"
              cx="18" cy="18" r="16"
              fill="none"
              strokeWidth="2"
              strokeDasharray={`${progressPercent}, 100`}
              transform="rotate(-90 18 18)"
            />
          </svg>
          <SyncOutlined className="refresh-indicator__icon" spin={isRefreshing} />
        </div>
      </Tooltip>

      {selectedBuild && (
        <BuildDetailModal
          build={selectedBuild}
          visible={!!selectedBuild}
          onClose={() => setSelectedBuild(null)}
        />
      )}
    </>
  );
};

export default React.memo(StatusGrid);
